import { useState, useEffect } from "react";
import { Loader2, XCircle, CalendarClock, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export default function BookingsTab({ onChange }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reschedule, setReschedule] = useState(null);
  const [days, setDays] = useState([]);
  const [slots, setSlots] = useState([]);
  const [rDate, setRDate] = useState("");
  const [rTime, setRTime] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(data.bookings);
    } catch {
      toast.error("Error al cargar reservas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar esta reserva? La hora volverá a quedar libre.")) return;
    try {
      await api.patch(`/admin/bookings/${id}/cancel`);
      toast.success("Reserva cancelada.");
      load();
      onChange?.();
    } catch {
      toast.error("No se pudo cancelar.");
    }
  };

  const openReschedule = async (b) => {
    setReschedule(b);
    setRDate("");
    setRTime("");
    const { data } = await api.get("/availability/days");
    setDays(data.days);
  };

  const pickDate = async (date) => {
    setRDate(date);
    setRTime("");
    const { data } = await api.get("/availability/slots", { params: { date } });
    setSlots(data.slots);
  };

  const confirmReschedule = async () => {
    if (!rDate || !rTime) return;
    try {
      await api.patch(`/admin/bookings/${reschedule.id}/reschedule`, { date: rDate, time: rTime });
      toast.success("Reserva reprogramada.");
      setReschedule(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reprogramar.");
    }
  };

  return (
    <div data-testid="bookings-tab">
      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-white/40 text-sm py-16"><Loader2 className="animate-spin" size={16} /> Cargando...</div>
        ) : bookings.length === 0 ? (
          <p className="text-center text-white/40 text-sm py-16">No hay reservas todavía.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-white/40 font-display">
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3 hidden md:table-cell">Contacto</th>
                  <th className="px-4 py-3">Fecha y hora</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-white/5">
                    <td className="px-4 py-3 text-white font-medium">{b.lead_name}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-white/60 text-xs">
                      <p className="flex items-center gap-1"><Mail size={12} /> {b.lead_email}</p>
                      <p className="flex items-center gap-1"><Phone size={12} /> {b.lead_phone}</p>
                    </td>
                    <td className="px-4 py-3 text-white/80 text-xs">{b.date_label || b.date} · <span className="text-[#D4AF37] font-bold">{b.time}</span></td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-display uppercase ${b.status === "confirmada" ? "text-[#34D399]" : "text-white/40"}`}>{b.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      {b.status === "confirmada" && (
                        <div className="flex items-center justify-end gap-2">
                          <button data-testid={`reschedule-${b.id}`} onClick={() => openReschedule(b)} className="text-[#D4AF37] hover:opacity-80" title="Reprogramar"><CalendarClock size={16} /></button>
                          <button data-testid={`cancel-${b.id}`} onClick={() => cancel(b.id)} className="text-red-400 hover:opacity-80" title="Cancelar"><XCircle size={16} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {reschedule && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setReschedule(null)}>
          <div className="bg-[#0A0A0A] border border-white/15 rounded-sm w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold uppercase text-lg mb-1">Reprogramar reserva</h3>
            <p className="text-white/50 text-sm mb-5">{reschedule.lead_name}</p>
            <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">Día</p>
            <select value={rDate} onChange={(e) => pickDate(e.target.value)} className="w-full bg-black border border-white/15 rounded-sm px-3 py-2.5 text-sm mb-4 focus:border-[#D4AF37] focus:outline-none">
              <option value="">Selecciona un día</option>
              {days.map((d) => <option key={d.date} value={d.date}>{d.label}</option>)}
            </select>
            {rDate && (
              <>
                <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">Hora</p>
                <div className="grid grid-cols-4 gap-2 mb-5">
                  {slots.length === 0 ? <p className="col-span-4 text-white/40 text-xs">Sin horas libres.</p> : slots.map((t) => (
                    <button key={t} onClick={() => setRTime(t)} className={`py-2 rounded-sm border text-xs font-display ${rTime === t ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/15 text-white/60"}`}>{t}</button>
                  ))}
                </div>
              </>
            )}
            <div className="flex gap-3">
              <button onClick={() => setReschedule(null)} className="btn-outline-gold rounded-sm flex-1 py-2.5 text-xs">Cancelar</button>
              <button onClick={confirmReschedule} disabled={!rTime} className="btn-gold rounded-sm flex-1 py-2.5 text-xs disabled:opacity-40">Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
